import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import {
  Loader2, AlertTriangle, ArrowRight, ShieldCheck, Download, Gift,
  Copy, Check, TrendingUp, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Post-checkout conversion moment. Three jobs to do here, in priority:
 *   1. Confirm payment (poll /checkout/status until paid).
 *   2. Offer a Monthly → Annual upsell that saves £24.60/yr.
 *   3. Surface the VAT-compliant invoice download + the customer's referral code
 *      with one-click copy / share so each new customer becomes a soft referral.
 */
export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed | expired
  const [data, setData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  // Guards against firing the GA4 `checkout_completed` conversion twice -
  // once per component lifecycle even if React StrictMode double-invokes the
  // effect in dev or the user reloads the page after a successful poll.
  const conversionFiredRef = useRef(false);

  useSeo({
    title: status === "paid" ? "Thank you for your payment" : "Confirming your payment",
    noindex: true,
  });

  useEffect(() => {
    let cancelled = false;
    let timer;

    const run = async () => {
      if (!sessionId) {
        if (!cancelled) setStatus("failed");
        return;
      }
      const poll = async (n = 0) => {
        if (n >= 8) {
          if (!cancelled) setStatus("failed");
          return;
        }
        try {
          const { data } = await api.get(`/checkout/status/${sessionId}`);
          if (cancelled) return;
          setData(data);
          if (data.payment_status === "paid") {
            setStatus("paid");
            // GA4 `checkout_completed` - fires exactly once per successful
            // payment confirmation. `plan_type` mirrors the pricing-page copy
            // so the funnel report matches the button label the user clicked.
            // `value` + `currency` follow GA4's ecommerce conventions so this
            // event can be marked as a Conversion with revenue attribution.
            if (
              !conversionFiredRef.current &&
              typeof window !== "undefined" &&
              typeof window.gtag === "function"
            ) {
              conversionFiredRef.current = true;
              const paidPlan = data?.metadata?.plan_id;
              const planSuffix = paidPlan === "monthly" ? "monthly" : "annually";
              window.gtag("event", `checkout_completed_${planSuffix}`, {
                plan_type: paidPlan === "monthly" ? "buy now - monthly" : "buy now - annually",
                value: (data.amount_total || 0) / 100,
                currency: (data.currency || "gbp").toUpperCase(),
              });
            }
            return;
          }
          if (data.status === "expired") {
            setStatus("expired");
            return;
          }
          timer = setTimeout(() => {
            setAttempts(n + 1);
            poll(n + 1);
          }, 2000);
        } catch {
          if (!cancelled) setStatus("failed");
        }
      };
      await poll(0);
    };
    run();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId]);

  return (
    <main className="py-16 md:py-20 lg:py-24 xl:py-28 bg-[#f7f7f7] min-h-screen" data-testid="checkout-success-page">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
        {status === "checking" && (
          <div className="bnp-card p-10 text-center" data-testid="checkout-checking">
            <Loader2 className="mx-auto text-[#71edc9] animate-spin" size={32} />
            <h1 className="font-display text-2xl md:text-3xl text-[#04273d] mt-4 font-semibold">Confirming your payment…</h1>
            <p className="text-[#447694] mt-2 text-sm">This usually takes just a moment. Attempt {attempts + 1} / 8.</p>
          </div>
        )}

        {status === "paid" && (
          <PaidView sessionId={sessionId} data={data} />
        )}

        {(status === "failed" || status === "expired") && (
          <div className="bnp-card p-10 text-center" data-testid="checkout-failed">
            <AlertTriangle className="mx-auto text-amber-500" size={32} />
            <h1 className="font-display text-2xl md:text-3xl text-[#04273d] mt-4 font-semibold">
              {status === "expired" ? "Your checkout session expired." : "We couldn't confirm your payment."}
            </h1>
            <p className="text-[#447694] mt-3">
              No charge was made if the session expired. Please try again, or contact us if you believe this is an error.
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Link to="/pricing" className="btn-primary">Try again</Link>
              <Link to="/" className="btn-secondary">Back to home</Link>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

/* -------------------------------------------------------------------------- */
/* Paid view - orchestrates the three conversion features                     */
/* -------------------------------------------------------------------------- */

function PaidView({ sessionId, data }) {
  const planId = data?.metadata?.plan_id;
  return (
    <>
      <div className="bnp-card p-9 md:p-10 text-center" data-testid="checkout-paid">
        <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-[#71edc9]/30 mb-2">
          <ShieldCheck className="text-[#04273d]" size={28} />
        </div>
        <h1 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-[#04273d] mt-3 font-bold tracking-tight">
          Thank you for your payment.
        </h1>
        <p className="text-[#447694] mt-3 leading-relaxed max-w-lg mx-auto">
          Payment received. We&rsquo;ve emailed a confirmation with a summary of your details to {data?.metadata?.email || "your inbox"}.
          Our UK team will be in touch within <strong className="text-[#04273d]">2&ndash;3 working days</strong> to walk you through your account and confirm everything is in order.
        </p>

        {/* Transaction details */}
        <div className="mt-6 bg-[#f7f7f7] border border-[#9ac7f7]/30 rounded-2xl p-4 text-left max-w-md mx-auto" data-testid="checkout-transaction-card">
          <p className="font-mono-brand text-[10px] text-[#447694] tracking-widest uppercase mb-2">Transaction</p>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <span className="text-[#447694]">Amount</span>
            <span className="text-[#04273d] font-mono-brand">£{((data.amount_total || 0) / 100).toFixed(2)} {(data.currency || "").toUpperCase()}</span>
            <span className="text-[#447694]">Plan</span>
            <span className="text-[#04273d]">{data.metadata?.plan_label || "-"}</span>
            <span className="text-[#447694]">Session</span>
            <span className="text-[#04273d] font-mono-brand text-xs truncate">{sessionId?.slice(0, 24)}…</span>
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-5">
        {/* 1. Monthly → Annual upsell (only if on monthly) */}
        {planId === "monthly" && (
          <AnnualUpsellCard sessionId={sessionId} />
        )}

        {/* 2. VAT invoice download */}
        <InvoiceDownloadCard sessionId={sessionId} email={data?.metadata?.email} />

        {/* 3. Referral code share */}
        <ReferralShareCard sessionId={sessionId} email={data?.metadata?.email} />

        {/* Footer nav */}
        <div className="bnp-card p-5 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-[#447694]">Manage your subscription any time at <Link to="/account" className="text-[#0d9b80] underline">/account</Link>.</p>
          <Link to="/" className="btn-secondary text-sm" data-testid="checkout-back-home">
            Back to home <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* 1. Annual upsell                                                           */
/* -------------------------------------------------------------------------- */

function AnnualUpsellCard({ sessionId }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const upgrade = async () => {
    setBusy(true);
    try {
      await api.post("/billing/switch-to-annual", { session_id: sessionId });
      setDone(true);
      toast.success("Switched to annual - you've saved £24.60.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't switch your plan just now. Please try from /account.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="bnp-card p-6 bg-[#71edc9]/20 border-[#71edc9]/50" data-testid="upsell-success">
        <div className="flex items-center gap-3">
          <Sparkles className="text-[#0d9b80]" size={22} />
          <p className="font-display font-semibold text-[#04273d]">You&rsquo;re now on the annual plan. Saving £24.60 a year.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-3xl p-6 md:p-7 bg-gradient-to-br from-[#04273d] to-[#0a3b59] text-white relative overflow-hidden" data-testid="upsell-card">
      <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-[#71edc9]/15 blur-2xl" />
      <div className="flex items-start gap-4 relative">
        <div className="h-12 w-12 rounded-2xl bg-[#71edc9]/25 flex items-center justify-center shrink-0">
          <TrendingUp size={24} className="text-[#71edc9]" />
        </div>
        <div className="flex-1">
          <span className="inline-flex items-center gap-1.5 bg-[#71edc9] text-[#04273d] px-2.5 py-1 rounded-full text-[10px] font-display font-bold tracking-wider uppercase">
            One-click upgrade
          </span>
          <h3 className="font-display text-2xl mt-3 font-bold">Switch to annual and save £24.60</h3>
          <p className="text-sm text-[#9ac7f7] mt-2 leading-relaxed">
            Same protection, same legal assistance. Pay £155.40 once a year instead of £15 a month - that&rsquo;s
            <strong className="text-[#71edc9]"> two months free</strong>. We&rsquo;ll credit your current monthly payment
            against the annual bill, so you only pay the difference today.
          </p>
          <button
            type="button"
            onClick={upgrade}
            disabled={busy}
            className="mt-5 inline-flex items-center gap-2 bg-[#71edc9] text-[#04273d] hover:bg-white transition-colors px-6 py-3.5 rounded-full font-display font-bold text-sm disabled:opacity-60"
            data-testid="upsell-button"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
            {busy ? "Upgrading…" : "Switch to annual now"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* 2. Invoice download                                                        */
/* -------------------------------------------------------------------------- */

function InvoiceDownloadCard({ sessionId, email }) {
  const [busy, setBusy] = useState(false);
  const [invoice, setInvoice] = useState(null);

  const fetchInvoice = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/latest-invoice", { session_id: sessionId, email });
      setInvoice(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Invoice isn't ready yet - try again in a minute.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bnp-card p-6" data-testid="invoice-card">
      <div className="flex items-start gap-4">
        <div className="h-11 w-11 rounded-2xl bg-[#71edc9]/30 flex items-center justify-center shrink-0">
          <Download size={20} className="text-[#04273d]" />
        </div>
        <div className="flex-1">
          <p className="font-display font-bold text-[#04273d] text-lg">Download your VAT receipt</p>
          <p className="text-sm text-[#447694] mt-1.5 leading-relaxed">
            HMRC-compliant invoice from Stripe with the net amount, VAT and your business address. Forward
            straight to your accountant.
          </p>
          {!invoice ? (
            <button
              type="button"
              onClick={fetchInvoice}
              disabled={busy}
              className="btn-primary mt-4 text-sm"
              data-testid="invoice-fetch-button"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              {busy ? "Fetching…" : "Get my receipt"}
            </button>
          ) : (
            <div className="mt-4 flex flex-wrap gap-2.5" data-testid="invoice-links">
              {invoice.invoice_pdf && (
                <a href={invoice.invoice_pdf} target="_blank" rel="noopener noreferrer" className="btn-primary text-sm" data-testid="invoice-pdf-link">
                  Download PDF <Download size={14} />
                </a>
              )}
              {invoice.hosted_invoice_url && (
                <a href={invoice.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="btn-secondary text-sm" data-testid="invoice-view-link">
                  View online
                </a>
              )}
              <span className="text-xs text-[#447694] self-center ml-1">
                Invoice {invoice.number} · £{Number(invoice.total || 0).toFixed(2)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* 3. Referral code share                                                     */
/* -------------------------------------------------------------------------- */

function ReferralShareCard({ sessionId, email }) {
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const { data } = await api.post("/billing/referral-info", { session_id: sessionId, email });
        if (!cancelled) setInfo(data);
      } catch {
        /* silent - referral block is optional */
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [sessionId, email]);

  if (loading || !info?.referral_code) return null;

  const shareUrl = `${window.location.origin}/pricing?ref=${info.referral_code}`;
  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Couldn't copy automatically. Long-press to copy manually.");
    }
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Business Name Protection",
          text: `I'm using Business Name Protection to legally protect my business name. Use my code ${info.referral_code} to get your first period free.`,
          url: shareUrl,
        });
      } catch { /* user cancelled */ }
    } else {
      copy(shareUrl);
    }
  };

  return (
    <div className="rounded-3xl p-6 md:p-7 bg-[#71edc9]/25 border-2 border-[#71edc9]/40 relative overflow-hidden" data-testid="referral-card">
      <div className="flex items-start gap-4">
        <div className="h-11 w-11 rounded-2xl bg-[#04273d] flex items-center justify-center shrink-0">
          <Gift size={20} className="text-[#71edc9]" />
        </div>
        <div className="flex-1">
          <p className="font-display font-bold text-[#04273d] text-lg">Get a free month - refer a friend</p>
          <p className="text-sm text-[#04273d]/85 mt-1.5 leading-relaxed">
            Share this code. They get their first period free; you get a free month credited automatically when they pay
            their first invoice.
          </p>

          <div className="mt-4 flex flex-col sm:flex-row gap-2.5">
            <div className="flex-1 bg-white border-2 border-[#04273d]/15 rounded-2xl px-4 py-3 flex items-center justify-between gap-3">
              <span className="font-mono-brand text-lg font-bold tracking-wider text-[#04273d]" data-testid="referral-code">
                {info.referral_code}
              </span>
              <button
                type="button"
                onClick={() => copy(info.referral_code)}
                className="text-[#0d9b80] hover:text-[#04273d] transition-colors"
                aria-label="Copy referral code"
                data-testid="referral-copy-code"
              >
                {copied ? <Check size={18} /> : <Copy size={18} />}
              </button>
            </div>
            <button
              type="button"
              onClick={shareNative}
              className="bg-[#04273d] text-white hover:bg-[#0a3b59] transition-colors px-5 py-3 rounded-2xl font-display font-bold text-sm whitespace-nowrap"
              data-testid="referral-share-button"
            >
              Share my code
            </button>
          </div>

          <p className="text-[11.5px] text-[#447694] mt-3 break-all" data-testid="referral-share-url">
            {shareUrl}
          </p>

          {info.referred_count > 0 && (
            <p className="text-xs text-[#0d9b80] font-semibold mt-2" data-testid="referral-count">
              You&rsquo;ve already referred {info.referred_count} {info.referred_count === 1 ? "friend" : "friends"}.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
